/**
 * Reading what the API said about a results upload.
 *
 * Pure functions, no Angular, so they can be tested without the broken Karma runner.
 *
 * ## Why a 2xx can arrive in the error callback
 *
 * `lab-manager.service.ts` posts with no `responseType`, so Angular defaults to `json`.
 * When the backend answers 2xx with a body that is not JSON, the parser throws and
 * Angular emits an `HttpErrorResponse` whose `status` is the original 2xx and whose
 * `error.text` holds the raw body. The old handler carried the comment
 * "i dont know why, but the successful message pops up here" for exactly this.
 *
 * So the error callback is not proof of failure. `isSuccessStatus` is the one line that
 * sorts it out. Switching to `responseType: 'text'` would also stop the throw, at the
 * price of hand-parsing every JSON response forever.
 */

import { LabUploadProblem, LabUploadResult, UploadVerdict } from '../models/lab-upload-result';

/** 2xx. The point is that an HttpErrorResponse carrying one is a success. */
export function isSuccessStatus(status: unknown): boolean {
  return typeof status === 'number' && status >= 200 && status < 300;
}

/**
 * A file the server read and refused.
 *
 * 422 is not a transport failure: it means the upload worked, the server parsed the
 * file, found something that must not be stored, rolled the transaction back and
 * returned the problems in the body. Angular still routes it to the error callback
 * because of the status code, so without this the report is thrown away and the user
 * is shown a network error instead of what is wrong with their spreadsheet.
 */
export function isRejectedUpload(status: unknown): boolean {
  return status === 422;
}

/**
 * The report out of whatever the response carried, or null when there is nothing in it.
 *
 * Handles an object, a JSON string (the 2xx-parse-failure path above), and an empty or
 * unparseable body. Never throws: a malformed body is a reason to say "I do not know
 * what happened", not to break the upload screen.
 */
export function parseUploadBody(body: unknown): LabUploadResult | null {
  if (body === null || body === undefined) {
    return null;
  }

  if (typeof body === 'string') {
    const text = body.trim();
    if (!text) {
      return null;
    }
    try {
      const parsed = JSON.parse(text);
      return parsed && typeof parsed === 'object' ? (parsed as LabUploadResult) : null;
    } catch {
      return null;
    }
  }

  return typeof body === 'object' ? (body as LabUploadResult) : null;
}

/**
 * A message fit for a snackbar, out of whatever the error carried.
 *
 * Always a string. The previous code did `res?.error?.text ?? res?.error`, which handed
 * the snackbar an object on an ordinary JSON 500 and rendered "[object Object]".
 */
export function uploadErrorMessage(err: unknown, fallback = 'The results file could not be uploaded.'): string {
  const candidates: unknown[] = [];
  const error = (err as { error?: unknown })?.error;

  if (typeof error === 'string') {
    candidates.push(error);
  } else if (error && typeof error === 'object') {
    candidates.push((error as { text?: unknown }).text);
    candidates.push((error as { message?: unknown }).message);
  }
  candidates.push((err as { message?: unknown })?.message);

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return fallback;
}

/**
 * What to do about an upload.
 *
 * Severity comes from the server's own `severity` field, falling back to the counts.
 * **Never from matching on the message text.** Message wording is prose for a human and
 * will be reworded; if this matched on it, rewording a sentence would quietly turn a
 * hard block into a warning and nothing would report it.
 */
export function classifyUpload(result: LabUploadResult | null): UploadVerdict {
  if (!result) {
    return 'unknown';
  }

  const problems = result.problems ?? [];
  const blocking = problems.filter(p => p.severity === 'BLOCKING').length
    || (result.blockingCount ?? 0);

  if (blocking > 0 || result.accepted === false) {
    return 'blocking';
  }

  const warnings = problems.filter(p => p.severity === 'WARNING').length
    || (result.warningCount ?? 0);

  if (warnings > 0) {
    return 'warning';
  }

  // Nothing wrong reported, but nothing read either: the API told us nothing useful.
  if (result.rowsRead === undefined && problems.length === 0 && result.accepted === undefined) {
    return 'unknown';
  }

  return 'ok';
}

/** The problems, worst first, so the dialog leads with what has to be fixed. */
export function orderedProblems(result: LabUploadResult | null): LabUploadProblem[] {
  const problems = [...(result?.problems ?? [])];
  const rank = (p: LabUploadProblem) => (p.severity === 'BLOCKING' ? 0 : 1);
  return problems.sort((a, b) => rank(a) - rank(b) || (a.rowNumber ?? 0) - (b.rowNumber ?? 0));
}

/**
 * One line summarising the outcome, for the dialog heading.
 *
 * Deliberately plain. The user is about to be told their file was refused, which is
 * annoying enough without the copy being chirpy about it.
 */
export function summariseUpload(result: LabUploadResult | null, verdict: UploadVerdict): string {
  const problems = result?.problems ?? [];
  const blocking = problems.filter(p => p.severity === 'BLOCKING').length || (result?.blockingCount ?? 0);
  const warnings = problems.filter(p => p.severity === 'WARNING').length || (result?.warningCount ?? 0);

  if (verdict === 'blocking') {
    return `This file was not accepted, so nothing has changed. ${count(blocking, 'problem')} must be fixed before it can be uploaded.`;
  }

  if (verdict === 'warning') {
    return `The results were uploaded, but there ${warnings === 1 ? 'is' : 'are'} ${count(warnings, 'thing')} worth checking.`;
  }

  if (verdict === 'unknown') {
    return 'The file was uploaded, but the system did not report what was saved. Please check the Results tab.';
  }

  return 'The results were uploaded.';
}

function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}
